import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/PageHero";
import { useSeo, JsonLd } from "@/lib/seo";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, Phone } from "lucide-react";

const FAQS = [
  { q: "Intervenez-vous pour les mariages ?", a: "Oui, My Animation propose des prestations DJ mariage complètes : cérémonie, vin d'honneur, repas, ouverture de bal, soirée dansante, sonorisation et mise en lumière." },
  { q: "Fournissez-vous le matériel son et lumière ?", a: "Oui, la prestation peut inclure la sonorisation, l'éclairage, les micros pour discours ou cérémonie et la mise en lumière adaptée au lieu." },
  { q: "Proposez-vous un photobooth ?", a: "Oui, My Animation propose plusieurs forfaits photobooth avec photos numériques illimitées, impressions selon forfait et accès en ligne aux photos avec identifiants." },
  { q: "Intervenez-vous pour les entreprises ?", a: "Oui, My Animation intervient pour soirées d'entreprise, galas, séminaires, inaugurations, afterworks, arbres de Noël et événements professionnels." },
  { q: "Travaillez-vous avec les mairies et collectivités ?", a: "Oui, My Animation propose des prestations pour fêtes municipales, Fête de la Musique, 14 juillet, cérémonies, inaugurations et événements publics." },
  { q: "Peut-on personnaliser la playlist ?", a: "Oui, la programmation musicale est adaptée à l'événement, au public, aux moments clés et aux préférences des clients." },
  { q: "Peut-on demander des musiques à éviter ?", a: "Oui, il est possible de transmettre une liste de titres ou de styles à éviter afin de respecter l'ambiance souhaitée." },
  { q: "Proposez-vous des animations blind test ou karaoké ?", a: "Oui, My Animation propose des animations blind test, quiz et karaoké pour événements privés, bars, entreprises, associations et collectivités." },
  { q: "Dans quelles zones intervenez-vous ?", a: "My Animation intervient principalement dans les Hauts-de-France, le Nord, le Pas-de-Calais et la Belgique." },
  { q: "Comment demander un devis ?", a: "Il suffit de remplir le formulaire de demande de devis en indiquant le type d'événement, la date, le lieu, le nombre d'invités et les prestations souhaitées." },
];

export default function FAQ() {
  useSeo({
    title: "FAQ — DJ événementiel My Animation | Hauts-de-France",
    description:
      "Toutes les réponses sur les prestations DJ, photobooth, blind test, karaoké, mise en lumière et zones d'intervention de My Animation dans les Hauts-de-France.",
    path: "/faq",
    keywords: "FAQ DJ événementiel, questions DJ mariage, questions photobooth, My Animation",
  });

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <>
      <JsonLd data={faqJsonLd} id="jsonld-faq" />
      <PageHero
        eyebrow="FAQ"
        title={<>Vos questions, <span className="italic gold-text">nos réponses.</span></>}
        subtitle="Tout ce que vous devez savoir avant de réserver votre DJ événementiel, photobooth, animation blind test ou karaoké avec My Animation."
        img="https://images.pexels.com/photos/9665186/pexels-photo-9665186.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
      />

      <section className="py-20 md:py-28 px-6 md:px-10 lg:px-14 max-w-[1100px] mx-auto">
        <Accordion type="single" collapsible className="space-y-3" data-testid="faq-page-accordion">
          {FAQS.map((f, i) => (
            <AccordionItem
              key={f.q}
              value={`faq-${i}`}
              data-testid={`faq-page-item-${i}`}
              className="card-luxe !rounded-none px-6 md:px-8"
            >
              <AccordionTrigger className="font-serif-luxe text-xl md:text-2xl text-left hover:no-underline data-[state=open]:text-[#DCB457] py-6">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-white/70 text-base leading-relaxed pb-6">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <section className="border-t border-[#DCB457]/15 py-20 px-6 md:px-10 lg:px-14 max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h3 className="font-serif-luxe text-3xl md:text-4xl max-w-xl">
            Une question particulière ? <span className="italic gold-text">Contactez-nous.</span>
          </h3>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" data-testid="faq-cta-devis" className="btn-gold">
              Demander un devis <ArrowRight size={14} strokeWidth={1.4} />
            </Link>
            <a href="tel:0769224956" className="btn-ghost-gold">
              <Phone size={14} strokeWidth={1.4} /> Appeler maintenant
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
